import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

export const fetchTradingPairs = createAsyncThunk(
  'tradingPairs/fetchTradingPairs',
  async (params = {}) => {
    const response = await api.get('/api/trading-pairs/', { params });
    
    // Handle paginated response (DRF pagination)
    if (response.data && response.data.results && Array.isArray(response.data.results)) {
      return response.data.results;
    }
    
    // Handle non-paginated response (fallback)
    if (Array.isArray(response.data)) {
      return response.data;
    }
    
    // If unexpected format, return empty array to prevent crashes
    console.error('Unexpected response format from /api/trading-pairs/:', response.data);
    return [];
  }
);

export const createTradingPair = createAsyncThunk(
  'tradingPairs/create',
  async (pairData, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/trading-pairs/', pairData);
      return response.data;
    } catch (error) {
      console.error('Trading pair creation error:', error.response?.data || error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateTradingPair = createAsyncThunk(
  'tradingPairs/update',
  async ({ id, data }) => {
    const response = await api.patch(`/api/trading-pairs/${id}/`, data);
    return response.data;
  }
);

export const deleteTradingPair = createAsyncThunk(
  'tradingPairs/delete',
  async (id) => {
    await api.delete(`/api/trading-pairs/${id}/`);
    return id;
  }
);

const tradingPairSlice = createSlice({
  name: 'tradingPairs',
  initialState: {
    tradingPairs: [],
    loading: false,
    error: null,
    filters: {
      is_active: null, // Changed to null to show all trading pairs
      symbol: '',
      quote_asset: null,
    },
  },
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTradingPairs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTradingPairs.fulfilled, (state, action) => {
        state.loading = false;
        state.tradingPairs = action.payload;
      })
      .addCase(fetchTradingPairs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(createTradingPair.fulfilled, (state, action) => {
        state.tradingPairs.unshift(action.payload);
      })
      .addCase(updateTradingPair.fulfilled, (state, action) => {
        const index = state.tradingPairs.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.tradingPairs[index] = action.payload;
        }
      })
      .addCase(deleteTradingPair.fulfilled, (state, action) => {
        state.tradingPairs = state.tradingPairs.filter(p => p.id !== action.payload);
      });
  },
});

export const { setFilters } = tradingPairSlice.actions;
export default tradingPairSlice.reducer;